/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {setAccountTree} from '@ion/core/src/entities/Accounts';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {AccountLink} from '@ion/core/src/types/entities';
import {
  FilterChecklistValueBase,
  FilterTreeNodeBase,
  FilterTreeSearchResultBase
} from '@ion/dls/src/filters/types';
import {collectFilterTreeNodeKeys, getFilterTreeNodeKey} from '@ion/dls/src/filters/utils';
import {
  TrackingDrawerFilterOptionBase,
  TrackingFavoriteFilterState,
  TrackingFilterDrawerCategoryId,
  TrackingFilterNavigationRequest,
  TrackingFilterStateKey,
  TrackingGroupHierarchyMap,
  TrackingGroupsAssetsSelectedPayload,
  TrackingGroupsAssetsTreeChange,
  TrackingGroupsAssetsTreeNode
} from './types';

export const TRACKING_GROUP_NODE_TYPE = 'group';
export const TRACKING_ASSET_NODE_TYPE = 'asset';

export const getTrackingDrawerValueKey = (value: FilterChecklistValueBase) => value?.toString();

/**
 * Compares tracking filter selections without depending on order or duplicates.
 *
 * @param first  Defines the first selection collection.
 * @param second Defines the second selection collection.
 * @returns `true` when both collections contain the same selection keys.
 */
export const areTrackingFilterSelectionsEqual = (
  first: FilterChecklistValueBase[],
  second: FilterChecklistValueBase[]
) => {
  const firstKeys  = new Set(first.map(getTrackingDrawerValueKey));
  const secondKeys = new Set(second.map(getTrackingDrawerValueKey));
  return firstKeys.size === secondKeys.size &&
    Array.from(firstKeys).every(key => secondKeys.has(key));
};

/**
 * Converts applied filter selections into checklist values.
 *
 * ```typescript
 * import {getTrackingDrawerSelectedValues} from './utils';
 *
 * ...
 *
 * const selectedValues = getTrackingDrawerSelectedValues(items, getSelectedValue);
 * ```
 */
export const getTrackingDrawerSelectedValues = (
  selectedItems: any[],
  toSelectedValue: (selected: any) => FilterChecklistValueBase
) => selectedItems.map(item => toSelectedValue(item));

/**
 * Resolves the payload dispatched for a tracking drawer filter option.
 *
 * ```typescript
 * import {getTrackingDrawerPayload} from './utils';
 *
 * ...
 *
 * const payload = getTrackingDrawerPayload(option);
 * ```
 */
export const getTrackingDrawerPayload = (option?: TrackingDrawerFilterOptionBase) => (
  option?.payload ?? option?.value
);

/**
 * Creates an option lookup keyed by checklist value.
 *
 * ```typescript
 * import {createTrackingDrawerOptionLookup} from './utils';
 *
 * ...
 *
 * const optionLookup = createTrackingDrawerOptionLookup(options);
 * ```
 */
export const createTrackingDrawerOptionLookup = (
  options: TrackingDrawerFilterOptionBase[]
) => (
  options.reduce((lookup, option) => ({
    ...lookup,
    [getTrackingDrawerValueKey(option.value)] : option
  }), {} as Record<string, TrackingDrawerFilterOptionBase>)
);

/**
 * Resolves selected payloads from checklist values and option lookup.
 *
 * ```typescript
 * import {getTrackingDrawerSelectedPayloads} from './utils';
 *
 * ...
 *
 * const payloads = getTrackingDrawerSelectedPayloads(values, optionLookup);
 * ```
 */
export const getTrackingDrawerSelectedPayloads = (
  values: FilterChecklistValueBase[],
  optionLookup: Record<string, TrackingDrawerFilterOptionBase>
) => (
  values.map(value => getTrackingDrawerPayload(optionLookup[getTrackingDrawerValueKey(value)]))
    .filter(value => typeof value !== 'undefined')
);

export const identityTrackingDrawerSelectedValue = (selected: any): FilterChecklistValueBase => selected;

export const createTrackingFavoriteFilterBucket = (selected: any[] = []) => ({
  selected
});

/**
 * Creates a favorite filter state fragment for one checklist category.
 *
 * ```typescript
 * import {createTrackingFavoriteFilterState} from './utils';
 *
 * ...
 *
 * const state = createTrackingFavoriteFilterState('assetTypes', selected);
 * ```
 */
export const createTrackingFavoriteFilterState = (
  key: TrackingFilterStateKey,
  selected: any[] = []
): TrackingFavoriteFilterState => ({
  [key] : createTrackingFavoriteFilterBucket(selected)
});

/**
 * Resolves saved selections for one favorite filter category.
 *
 * ```typescript
 * import {getTrackingFavoriteSelectedItems} from './utils';
 *
 * ...
 *
 * const assetTypes = getTrackingFavoriteSelectedItems(state, 'assetTypes');
 * ```
 */
export const getTrackingFavoriteSelectedItems = (
  state: TrackingFavoriteFilterState,
  key: TrackingFilterStateKey
) => state?.[key]?.selected ?? [];

/**
 * Resolves the account object from either a direct account or an account link.
 *
 * ```typescript
 * import {getTrackingGroupAccount} from './utils';
 *
 * ...
 *
 * const account = getTrackingGroupAccount(accountLink);
 * ```
 */
export const getTrackingGroupAccount = (account: AccountLink) => (
  (account as any)?.subaccount ?? account
);

/**
 * Resolves the stable group id used by the tracking groups/assets tree.
 *
 * ```typescript
 * import {getTrackingGroupId} from './utils';
 *
 * ...
 *
 * const groupId = getTrackingGroupId(account);
 * ```
 */
export const getTrackingGroupId = (account: AccountLink) => (
  entityId((account as any)?.href)?.toString() ??
  (account as any)?.accountId?.toString() ??
  (account as any)?.id?.toString()
);

/**
 * Converts an account hierarchy into a filter tree group node.
 *
 * ```typescript
 * import {createTrackingGroupNode} from './utils';
 *
 * ...
 *
 * const node = createTrackingGroupNode(globalGroup);
 * ```
 */
export const createTrackingGroupNode = (accountLink: AccountLink): TrackingGroupsAssetsTreeNode => {
  const account = getTrackingGroupAccount(accountLink);
  const groupId = getTrackingGroupId(account);
  const name = (account as any)?.title ?? (account as any)?.name;

  return {
    children : ((account as any)?.subAccounts ?? []).map(createTrackingGroupNode),
    hasMore  : true,
    id       : groupId,
    label    : name,
    payload  : {
      id : Number(groupId),
      name
    },
    type : TRACKING_GROUP_NODE_TYPE
  };
};

/**
 * Converts an asset record into a filter tree asset node.
 *
 * ```typescript
 * import {createTrackingAssetNode} from './utils';
 *
 * ...
 *
 * const node = createTrackingAssetNode(asset, groupId);
 * ```
 */
export const createTrackingAssetNode = (
  asset: any,
  accountId: string | number
): TrackingGroupsAssetsTreeNode => {
  const name = asset?.name ?? asset?.assetName;

  return {
    id      : asset?.id,
    label   : name,
    payload : {
      accountId : Number(accountId),
      id        : Number(asset?.id),
      name
    },
    type : TRACKING_ASSET_NODE_TYPE
  };
};

/**
 * Creates the selected id used by the tree checklist for groups and assets.
 *
 * ```typescript
 * import {getTrackingSelectedNodeId} from './utils';
 *
 * ...
 *
 * const selectedId = getTrackingSelectedNodeId('group', 123);
 * ```
 */
export const getTrackingSelectedNodeId = (type: string, id: string | number) => `${type}:${id}`;

/**
 * Creates tree selected ids from applied group and asset filters.
 *
 * ```typescript
 * import {getTrackingGroupsAssetsSelectedIds} from './utils';
 *
 * ...
 *
 * const selectedIds = getTrackingGroupsAssetsSelectedIds(groups, assets);
 * ```
 */
export const getTrackingGroupsAssetsSelectedIds = (
  selectedGroups: any[],
  selectedAssets: any[]
) => ([
  ...selectedGroups.map(group => (
    getTrackingSelectedNodeId(TRACKING_GROUP_NODE_TYPE, group?.id ?? group)
  )),
  ...selectedAssets.map(asset => {
    const appliedAsset = normalizeTrackingAppliedAsset(asset);
    return getTrackingSelectedNodeId(TRACKING_ASSET_NODE_TYPE, appliedAsset?.id ?? appliedAsset);
  })
]);

/**
 * Indexes every node of a groups/assets tree by its tree key.
 *
 * ```typescript
 * import {indexTrackingTreeNodes} from './utils';
 *
 * ...
 *
 * const nodesByKey = indexTrackingTreeNodes(nodes);
 * ```
 *
 * @param nodes Defines the tree's root nodes.
 * @returns Every node in the tree keyed by its tree key.
 */
export const indexTrackingTreeNodes = <TNode extends FilterTreeNodeBase>(
  nodes: TNode[],
  index: Map<string, TNode> = new Map()
): Map<string, TNode> => {
  nodes.forEach(node => {
    index.set(getFilterTreeNodeKey(node), node);
    indexTrackingTreeNodes((node.children ?? []) as TNode[], index);
  });
  return index;
};

/**
 * Adds to tree selected ids everything a selected group is known to hold -
 * its subgroups and the assets loaded under them - since a group is applied
 * as a whole, without them, and would otherwise show only partly ticked.
 *
 * ```typescript
 * import {getTrackingWholeGroupSelectedIds} from './utils';
 *
 * ...
 *
 * const treeSelectedIds = getTrackingWholeGroupSelectedIds(appliedSelectedIds, getChildKeys);
 * ```
 *
 * @param selectedIds  Defines the applied tree selected ids.
 * @param getChildKeys Defines how the keys a group directly holds are read.
 * @returns The selected ids with every known descendant of a selected group.
 */
export const getTrackingWholeGroupSelectedIds = (
  selectedIds: string[],
  getChildKeys: (groupKey: string) => string[]
): string[] => {
  const treeSelectedIds = new Set(selectedIds);
  const groupKeys = selectedIds.filter(id => id.startsWith(`${TRACKING_GROUP_NODE_TYPE}:`));
  while (groupKeys.length) {
    getChildKeys(groupKeys.pop()).forEach(childKey => {
      if (!treeSelectedIds.has(childKey)) {
        treeSelectedIds.add(childKey);
        if (childKey.startsWith(`${TRACKING_GROUP_NODE_TYPE}:`)) {
          groupKeys.push(childKey);
        }
      }
    });
  }
  return Array.from(treeSelectedIds);
};

/**
 * Converts an applied asset filter entry into the drawer's own asset shape.
 * View History (from the asset details, an email or a notification) applies
 * the asset's device record instead, whose `id` is the device's and whose
 * `assetId` is the asset's - the drawer keys and filters assets by asset id.
 * A deep link's View History (an alert's, or the map's) applies the asset as
 * fetched instead, whose group comes only from its `account` link.
 *
 * ```typescript
 * import {normalizeTrackingAppliedAsset} from './utils';
 *
 * ...
 *
 * const asset = normalizeTrackingAppliedAsset(trackingFilters.assets.selected[0]);
 * ```
 *
 * @param asset An applied asset filter entry.
 * @returns The `{accountId, id, name}` asset, or the entry as it is when it
 *          already has that shape.
 */
export const normalizeTrackingAppliedAsset = (asset: any) => {
  const hasAccountId = asset?.accountId !== undefined && asset?.accountId !== null;
  // an asset as fetched carries its group only as its `account` link ...
  const accountId    = hasAccountId ? asset.accountId : entityId(asset?.account);
  let appliedAsset   = asset;
  if (asset?.assetId !== undefined && asset?.assetId !== null) {
    appliedAsset = {
      accountId,
      id   : Number(asset.assetId),
      name : asset.assetName ?? asset.name
    };
  } else if (!hasAccountId && asset?.account) {
    appliedAsset = {
      accountId,
      id   : Number(asset.id),
      name : asset.name
    };
  }
  return appliedAsset;
};

/**
 * Determines whether an applied asset filter entry was applied by View History
 * (from the asset details, a deep link, an email or a notification) - its
 * record is not yet in the drawer's own asset shape.
 *
 * ```typescript
 * import {isViewHistoryAppliedAsset} from './utils';
 *
 * ...
 *
 * const viewHistoryAsset = appliedAssets.find(isViewHistoryAppliedAsset);
 * ```
 *
 * @param asset An applied asset filter entry.
 * @returns `true` when View History applied it, otherwise `false`.
 */
export const isViewHistoryAppliedAsset = (asset: any): boolean => (
  Boolean(asset) && normalizeTrackingAppliedAsset(asset) !== asset
);

/**
 * Creates the selected payload dispatched for tracking group filters.
 *
 * ```typescript
 * import {createTrackingGroupsAssetsPayload} from './utils';
 *
 * ...
 *
 * const payload = createTrackingGroupsAssetsPayload(groups, assets);
 * ```
 */
export const createTrackingGroupsAssetsPayload = (
  selectedGroups: any[] = [],
  selectedAssets: any[] = []
): TrackingGroupsAssetsSelectedPayload => ({
  selectedAssets,
  selectedDevices : [],
  selectedGroups
});

/**
 * Creates an empty groups/assets selection payload.
 *
 * ```typescript
 * import {createEmptyTrackingGroupsAssetsPayload} from './utils';
 *
 * ...
 *
 * const payload = createEmptyTrackingGroupsAssetsPayload();
 * ```
 */
export const createEmptyTrackingGroupsAssetsPayload = () => createTrackingGroupsAssetsPayload();

// a group checked as a whole already stands for every subgroup and asset
// under it, so those are left out - it is one filter, one chip and counted
// once, however far it has been expanded ...
const getOutermostCheckedNodes = <TNode extends FilterTreeNodeBase>(nodes: TNode[]): TNode[] => {
  const coveredKeys = new Set(nodes.flatMap(node => (
    (node.children ?? []).flatMap(collectFilterTreeNodeKeys)
  )));
  return nodes.filter(node => !coveredKeys.has(getFilterTreeNodeKey(node)));
};

/**
 * Builds the dispatch payload from a tree checklist change event - a group
 * checked as a whole is applied on its own, without what it holds.
 *
 * ```typescript
 * import {getTrackingGroupsAssetsPayloadFromChange} from './utils';
 *
 * ...
 *
 * const payload = getTrackingGroupsAssetsPayloadFromChange(change);
 * ```
 */
export const getTrackingGroupsAssetsPayloadFromChange = (
  change: TrackingGroupsAssetsTreeChange
) => {
  const appliedNodes   = getOutermostCheckedNodes(change.selectedNodes);
  const selectedGroups = appliedNodes
    .filter(node => node.type === TRACKING_GROUP_NODE_TYPE)
    .map(node => node.payload);
  const selectedAssets = appliedNodes
    .filter(node => node.type === TRACKING_ASSET_NODE_TYPE)
    .map(node => node.payload);

  return createTrackingGroupsAssetsPayload(selectedGroups, selectedAssets);
};

/**
 * Resolves the selected count displayed by the tracking drawer category.
 *
 * ```typescript
 * import {getTrackingGroupsAssetsSelectedCount} from './utils';
 *
 * ...
 *
 * const count = getTrackingGroupsAssetsSelectedCount(payload);
 * ```
 */
export const getTrackingGroupsAssetsSelectedCount = (
  payload: TrackingGroupsAssetsSelectedPayload
) => payload.selectedGroups.length + payload.selectedAssets.length;

/**
 * Converts the groups/assets payload into the legacy favorite state format.
 *
 * ```typescript
 * import {createTrackingGroupsAssetsFavoriteState} from './utils';
 *
 * ...
 *
 * const state = createTrackingGroupsAssetsFavoriteState(payload);
 * ```
 */
export const createTrackingGroupsAssetsFavoriteState = (
  payload: TrackingGroupsAssetsSelectedPayload
): TrackingFavoriteFilterState => ({
  [TrackingFilterStateKey.Assets]  : createTrackingFavoriteFilterBucket(payload.selectedAssets),
  [TrackingFilterStateKey.Devices] : createTrackingFavoriteFilterBucket(payload.selectedDevices),
  [TrackingFilterStateKey.Groups]  : createTrackingFavoriteFilterBucket(payload.selectedGroups)
});

/**
 * Resolves the groups/assets payload from saved favorite state.
 *
 * ```typescript
 * import {getTrackingGroupsAssetsFavoritePayload} from './utils';
 *
 * ...
 *
 * const payload = getTrackingGroupsAssetsFavoritePayload(state);
 * ```
 */
export const getTrackingGroupsAssetsFavoritePayload = (
  state: TrackingFavoriteFilterState
): TrackingGroupsAssetsSelectedPayload => ({
  selectedAssets : getTrackingFavoriteSelectedItems(
    state,
    TrackingFilterStateKey.Assets
  ),
  selectedDevices : getTrackingFavoriteSelectedItems(
    state,
    TrackingFilterStateKey.Devices
  ),
  selectedGroups : getTrackingFavoriteSelectedItems(
    state,
    TrackingFilterStateKey.Groups
  )
});

/**
 * Resolves the selected root group from the global group selector value.
 *
 * ```typescript
 * import {getTrackingRootGroup} from './utils';
 *
 * ...
 *
 * const rootGroup = getTrackingRootGroup(globalGroup);
 * ```
 */
export const getTrackingRootGroup = (globalGroup: any) => (
  globalGroup?.globalGroup ?? globalGroup
);

/**
 * Flattens a group hierarchy into a lookup keyed by group id, recording
 * each group's display name and parent id - used to resolve a search
 * result's ancestor path for revealing it in the tree.
 *
 * ```typescript
 * import {flattenTrackingGroupHierarchy} from './utils';
 *
 * ...
 *
 * const hierarchy = flattenTrackingGroupHierarchy(rootGroup);
 * ```
 */
export const flattenTrackingGroupHierarchy = (
  rootGroup: AccountLink
): TrackingGroupHierarchyMap => {
  if (!rootGroup) {
    return {};
  }
  const accountTree = setAccountTree([rootGroup]);
  return Object.fromEntries(Object.entries(accountTree).map(([groupId, entry]) => [
    groupId,
    {
      id       : groupId,
      name     : entry.title,
      parentId : entry.parentId != null ? `${entry.parentId}` : null
    }
  ]));
};

/**
 * Builds the group id membership map `AssetTreeLoader.requestSearch`
 * expects, from a flattened group hierarchy.
 *
 * ```typescript
 * import {getTrackingGroupIdMap} from './utils';
 *
 * ...
 *
 * const groupIdMap = getTrackingGroupIdMap(hierarchy);
 * ```
 */
export const getTrackingGroupIdMap = (hierarchy: TrackingGroupHierarchyMap): Record<string, boolean> => (
  Object.fromEntries(Object.keys(hierarchy).map(groupId => [groupId, true]))
);

/**
 * Resolves the ordered ancestor node keys, from the root group down to
 * the requested group, using a flattened group hierarchy.
 *
 * ```typescript
 * import {getTrackingGroupNodePath} from './utils';
 *
 * ...
 *
 * const nodePath = getTrackingGroupNodePath(hierarchy, groupId);
 * ```
 */
export const getTrackingGroupNodePath = (
  hierarchy: TrackingGroupHierarchyMap,
  groupId: string
): string[] => {
  const path: string[] = [];
  let currentId: string | null = groupId;
  while (currentId && hierarchy[currentId]) {
    path.unshift(getTrackingSelectedNodeId(TRACKING_GROUP_NODE_TYPE, currentId));
    currentId = hierarchy[currentId].parentId;
  }
  return path;
};

/**
 * Resolves the ordered ancestor node keys, from the root group down to a
 * given asset's own key, using a flattened group hierarchy.
 *
 * ```typescript
 * import {getTrackingAssetNodePath} from './utils';
 *
 * ...
 *
 * const nodePath = getTrackingAssetNodePath(hierarchy, groupId, assetId);
 * ```
 */
export const getTrackingAssetNodePath = (
  hierarchy: TrackingGroupHierarchyMap,
  groupId: string,
  assetId: string
): string[] => [
  ...getTrackingGroupNodePath(hierarchy, groupId),
  getTrackingSelectedNodeId(TRACKING_ASSET_NODE_TYPE, assetId)
];

/**
 * Determines the tree path of a groups/assets selection's first item - its
 * first asset, else its first group - e.g. to reveal a loaded favorite's
 * selection in the tree.
 *
 * ```typescript
 * import {getTrackingFirstSelectionNodePath} from './utils';
 *
 * ...
 *
 * const nodePath = getTrackingFirstSelectionNodePath(groupHierarchy, payload);
 * ```
 *
 * @param hierarchy The flattened group hierarchy.
 * @param payload   The groups/assets selection.
 * @returns The node path, or `null` when nothing is selected.
 */
export const getTrackingFirstSelectionNodePath = (
  hierarchy: TrackingGroupHierarchyMap,
  payload: TrackingGroupsAssetsSelectedPayload
): string[] | null => {
  const asset = normalizeTrackingAppliedAsset(payload?.selectedAssets?.[0]);
  const group = payload?.selectedGroups?.[0];
  let nodePath: string[] | null = null;
  if (asset) {
    nodePath = getTrackingAssetNodePath(hierarchy, `${asset.accountId}`, `${asset.id}`);
  } else if (group) {
    nodePath = getTrackingGroupNodePath(hierarchy, `${group?.id ?? group}`);
  }
  return nodePath;
};

/**
 * Converts a matched group into a tree checklist search result.
 *
 * ```typescript
 * import {createTrackingGroupSearchResult} from './utils';
 *
 * ...
 *
 * const result = createTrackingGroupSearchResult(hierarchy, groupId, label);
 * ```
 */
export const createTrackingGroupSearchResult = (
  hierarchy: TrackingGroupHierarchyMap,
  groupId: string,
  label: string
): FilterTreeSearchResultBase => ({
  isAsset  : false,
  key      : getTrackingSelectedNodeId(TRACKING_GROUP_NODE_TYPE, groupId),
  label,
  nodePath : getTrackingGroupNodePath(hierarchy, groupId)
});

export interface TrackingAssetSearchResultOptions {
  assetId: string;
  groupId: string;
  hierarchy: TrackingGroupHierarchyMap;
  label: string;
}

/**
 * Converts a matched asset into a tree checklist search result.
 *
 * ```typescript
 * import {createTrackingAssetSearchResult} from './utils';
 *
 * ...
 *
 * const result = createTrackingAssetSearchResult({assetId, groupId, hierarchy, label});
 * ```
 */
export const createTrackingAssetSearchResult = ({
  assetId,
  groupId,
  hierarchy,
  label
}: TrackingAssetSearchResultOptions): FilterTreeSearchResultBase => ({
  groupLabel : hierarchy[groupId]?.name,
  isAsset    : true,
  key        : getTrackingSelectedNodeId(TRACKING_ASSET_NODE_TYPE, assetId),
  label,
  nodePath   : getTrackingAssetNodePath(hierarchy, groupId, assetId)
});

/**
 * Runs a drawer filter's navigation away from Tracking through the drawer's
 * leave guard when one is given, otherwise at once.
 *
 * ```typescript
 * import {requestTrackingFilterNavigation} from './utils';
 *
 * ...
 *
 * requestTrackingFilterNavigation(navigateToAdmin, onRequestNavigate);
 * ```
 *
 * @param navigate          The navigation to run.
 * @param onRequestNavigate The drawer's leave guard, when there is one.
 */
export const requestTrackingFilterNavigation = (
  navigate: () => void,
  onRequestNavigate?: TrackingFilterNavigationRequest
) => {
  if (onRequestNavigate) {
    onRequestNavigate(navigate);
  } else {
    navigate();
  }
};

// the categories the Tracking Filter hides while a History report runs ...
const HISTORY_HIDDEN_CATEGORY_IDS: string[] = [
  TrackingFilterDrawerCategoryId.Geofence,
  TrackingFilterDrawerCategoryId.Routes,
  TrackingFilterDrawerCategoryId.AssetStatus
];

/**
 * Leaves out the Tracking Filter categories hidden while a History report
 * runs - Geofence, Routes and Asset Status - and keeps every category
 * otherwise.
 *
 * ```typescript
 * import {getVisibleTrackingFilterCategories} from './utils';
 *
 * ...
 *
 * const visibleCategories = getVisibleTrackingFilterCategories(categories, isHistoryRunning);
 * ```
 *
 * @param categories       Defines the drawer's categories, in order.
 * @param isHistoryRunning Defines whether a History report is running.
 * @returns The categories the drawer shows, in the same order.
 */
export const getVisibleTrackingFilterCategories = <TCategory extends {id: string}>(
  categories: TCategory[],
  isHistoryRunning: boolean
): TCategory[] => {
  let visibleCategories = categories;
  if (isHistoryRunning) {
    visibleCategories = categories.filter(({id}) => !HISTORY_HIDDEN_CATEGORY_IDS.includes(id));
  }
  return visibleCategories;
};

/**
 * Folds a reorder of the visible categories back into the full category
 * order - each visible category takes the next slot a visible one held, and
 * a hidden category keeps its own, so it comes back where it was once shown
 * again. A visible category the full order did not know goes at the end.
 *
 * ```typescript
 * import {mergeVisibleTrackingFilterCategoryOrder} from './utils';
 *
 * ...
 *
 * mergeVisibleTrackingFilterCategoryOrder(['a', 'hidden', 'b'], ['b', 'a']);
 * // ['b', 'hidden', 'a'] ...
 * ```
 *
 * @param fullOrder    Defines every category id, in its current order.
 * @param visibleOrder Defines the visible category ids, in their new order.
 * @returns Every category id, in the new order.
 */
export const mergeVisibleTrackingFilterCategoryOrder = (
  fullOrder: string[],
  visibleOrder: string[]
): string[] => {
  const visibleIds = new Set(visibleOrder);
  const pending    = [...visibleOrder];
  const merged     = fullOrder.map(id => (visibleIds.has(id) ? pending.shift() as string : id));
  return [...merged, ...pending];
};
